import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  CircularProgress
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import AdminTable from "../AdminTable";
import { format } from "date-fns";
import { ru, enUS } from "date-fns/locale";
import ButtonBB from "@View/components/buttons/ButtonBB";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useFileLoader } from "@Utils/useFileLoader";
import { 
  getIntegrationFiles, 
  uploadIntegrationFile, 
  downloadIntegrationFile 
} from "@Dao/integrationFilesApi";
import { FileType } from "@Constants/types";

const useStyles = makeStyles((theme) => ({
  tableHeader: {
    backgroundColor: "#F3F5F7",
  },
  actionButton: {
    margin: theme.spacing(0, 0.5),
    minWidth: 100,
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  noFileChip: {
    backgroundColor: "#F3F5F7",
    color: "#54606F",
    fontWeight: 600,
  },
  hasFileChip: {
    backgroundColor: "#E9F5EE",
    color: "#43A56C",
    fontWeight: 600,
  },
  errorContainer: {
    marginBottom: theme.spacing(2),
  },
}));

const FILE_TYPE_CONFIG = [
  {
    type: FileType.OneSPlugin,
    key: "OneSPlugin",
    acceptedFormats: [".epf"],
  },
{
    type: FileType.UserManualOneSPlugin,
    key: "UserManualOneSPlugin",
    acceptedFormats: [".pdf", ".doc", ".docx"],
  },
  {
    type: FileType.UserManualOneSPluginFresh,
    key: "UserManualOneSPluginFresh",
    acceptedFormats: [".pdf", ".doc", ".docx"],
  },
  {
    type: FileType.UserManualFreshWithDigitalSignature,
    key: "UserManualFreshWithDigitalSignature",
    acceptedFormats: [".pdf", ".doc", ".docx"],
  },
  {
    type: FileType.UserManualOneSFinOtchetnost,
    key: "UserManualOneSFinOtchetnost",
    acceptedFormats: [".pdf", ".doc", ".docx"],
  },
  {
    type: FileType.UserManualSmartan,
    key: "UserManualSmartan",
    acceptedFormats: [".pdf", ".doc", ".docx"],
  },
  {
    type: FileType.FAQ,
    key: "FAQ",
    acceptedFormats: [".pdf", ".doc", ".docx"],
  }
];

const IntegrationFilesTab = () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { openChooseFileWindow } = useFileLoader();
  
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [error, setError] = useState(null);

  const locale = i18n.language === "ru" ? ru : enUS;

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const integrationFiles = await getIntegrationFiles();
      setFiles(integrationFiles);
    } catch (err) {
      setError(err.message || "Failed to load files");
    } finally {
      setLoading(false);
    }
  };

  const getFileByType = (fileType) => {
    return files.find(file => file.fileType === FILE_TYPE_CONFIG.find(config => config.type === fileType)?.key);
  };

  const validateFileType = (file, acceptedFormats) => {
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    
    return acceptedFormats.some(format => 
      format.toLowerCase() === fileExtension
    );
  };

  const handleUpload = (fileType, acceptedFormats) => {
    const acceptString = acceptedFormats.join(",");
    
    openChooseFileWindow(
      { accept: acceptString },
      async (selectedFiles) => {
        if (selectedFiles.length === 0) return;
        
        const file = selectedFiles[0];
        
        // Validate file type
        if (!validateFileType(file, acceptedFormats)) {
          setError(`Неподдерживаемый тип файла. Допустимые форматы: ${acceptedFormats.join(", ")}`);
          return;
        }
        
        setUploading(prev => ({ ...prev, [fileType]: true }));
        setError(null); // Clear any previous errors
        
        try {
          await uploadIntegrationFile(file, fileType);
          await loadFiles(); // Refresh the list
        } catch (err) {
          setError(err.message || "Upload failed");
        } finally {
          setUploading(prev => ({ ...prev, [fileType]: false }));
        }
      }
    );
  };

  const handleDownload = async (fileId) => {
    try {
      await downloadIntegrationFile(fileId);
    } catch (err) {
      setError(err.message || "Download failed");
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "dd.MM.yyyy HH:mm", { locale });
    } catch {
      return dateString;
    }
  };


  return (
    <Box>      
      {error && (
        <div className={classes.errorContainer}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      <AdminTable>
        <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell>Тип файла</TableCell>
              <TableCell>{t("integrationFiles.fileName")}</TableCell>
              <TableCell>{t("integrationFiles.uploadedDate")}</TableCell>
              <TableCell align="center">Статус</TableCell>
              <TableCell>Допустимые форматы</TableCell>
              <TableCell align="center">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {FILE_TYPE_CONFIG.map(({ type, key, acceptedFormats }) => {
              const file = getFileByType(type);
              const isUploading = uploading[type];
              
              return (
                <TableRow key={type}>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {t(`integrationFiles.fileTypes.${key}`)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {file ? file.fileName : "-"}
                  </TableCell>
                  <TableCell>
                    {file ? formatDate(file.createdAt) : "-"}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={file ? "Загружен" : t("integrationFiles.noFile")}
                      className={file ? classes.hasFileChip : classes.noFileChip}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {acceptedFormats.join(", ")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
                      <ButtonBB
                        startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                        disabled={isUploading}
                        onClick={() => handleUpload(type, acceptedFormats)}
                        className={classes.actionButton}
                        style={{ fontSize: "12px", padding: "6px 12px" }}
                      >
                        {t("integrationFiles.actions.upload")}
                      </ButtonBB>
                      {file && (
                        <ButtonBB
                          startIcon={<GetAppIcon />}
                          onClick={() => handleDownload(file.id)}
                          className={classes.actionButton}
                          style={{ fontSize: "12px", padding: "6px 12px" }}
                        >
                          {t("integrationFiles.actions.download")}
                        </ButtonBB>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
      </AdminTable>
    </Box>
  );
};

export default IntegrationFilesTab;